/**
 * Dates, times and money, in one place.
 *
 * Two rules from the house standards are enforced here rather than trusted to
 * every call site:
 *   1. Storage and transport are 24h "HH:MM"; every string a human reads comes
 *      out of `fmtTime12` as "7:30am".
 *   2. Nothing here emits an em dash. Ranges use "to", which also reads better
 *      out loud on a roster.
 *
 * All date maths runs on plain yyyy-mm-dd strings through UTC midnight, the
 * same trick the engine uses (`bucketForDate`), so a local timezone can never
 * shift a roster day by one.
 */

export type IsoDate = string; // yyyy-mm-dd
export type Hhmm = string; // HH:MM 24h

const DAY_MS = 86_400_000;

export const DAY_NAMES = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'] as const;
export const DAY_SHORT = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const;
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const;
const MONTHS_LONG = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
] as const;

function utc(iso: IsoDate): Date {
  return new Date(`${iso}T00:00:00Z`);
}

/** yyyy-mm-dd for a JS Date, read in the browser's own local calendar. */
export function toIso(d: Date): IsoDate {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function today(): IsoDate {
  return toIso(new Date());
}

export function addDays(iso: IsoDate, n: number): IsoDate {
  return new Date(utc(iso).getTime() + n * DAY_MS).toISOString().slice(0, 10);
}

export function diffDays(a: IsoDate, b: IsoDate): number {
  return Math.round((utc(a).getTime() - utc(b).getTime()) / DAY_MS);
}

/** 0 = Monday, matching the roster grid and the tenant default weekStartsOn. */
export function dayIndex(iso: IsoDate): number {
  return (utc(iso).getUTCDay() + 6) % 7;
}

/** The Monday of the week a date falls in. */
export function weekStart(iso: IsoDate): IsoDate {
  return addDays(iso, -dayIndex(iso));
}

export function weekDates(monday: IsoDate): IsoDate[] {
  return Array.from({ length: 7 }, (_, i) => addDays(monday, i));
}

export function isWeekend(iso: IsoDate): boolean {
  return dayIndex(iso) >= 5;
}

/** "12 Aug" */
export function fmtDayMonth(iso: IsoDate): string {
  const d = utc(iso);
  return `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]}`;
}

/** "Tuesday 12 August 2026" */
export function fmtDateLong(iso: IsoDate): string {
  const d = utc(iso);
  return `${DAY_NAMES[dayIndex(iso)]} ${d.getUTCDate()} ${MONTHS_LONG[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}

/** "12 Aug 2026" */
export function fmtDateMedium(iso: IsoDate): string {
  const d = utc(iso);
  return `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}

/** "11 to 17 Aug 2026", collapsing a shared month and year. */
export function fmtDateRange(from: IsoDate, to: IsoDate): string {
  const a = utc(from);
  const b = utc(to);
  const sameYear = a.getUTCFullYear() === b.getUTCFullYear();
  const sameMonth = sameYear && a.getUTCMonth() === b.getUTCMonth();
  if (sameMonth) return `${a.getUTCDate()} to ${b.getUTCDate()} ${MONTHS[b.getUTCMonth()]} ${b.getUTCFullYear()}`;
  if (sameYear) return `${fmtDayMonth(from)} to ${fmtDayMonth(to)} ${b.getUTCFullYear()}`;
  return `${fmtDateMedium(from)} to ${fmtDateMedium(to)}`;
}

/**
 * The one function allowed to turn a stored time into something a person reads.
 * "07:30" becomes "7:30am"; a whole hour drops the ":00" so a roster card says
 * "7am to 4pm" rather than "7:00am to 4:00pm".
 */
export function fmtTime12(hhmm: Hhmm | null | undefined): string {
  if (!hhmm) return '';
  const parts = hhmm.split(':');
  const h = Number(parts[0]);
  const m = Number(parts[1] ?? '0');
  if (Number.isNaN(h) || Number.isNaN(m)) return '';
  const suffix = h < 12 ? 'am' : 'pm';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return m === 0 ? `${h12}${suffix}` : `${h12}:${String(m).padStart(2, '0')}${suffix}`;
}

/** "7:30am to 4pm" */
export function fmtTimeRange(start: Hhmm, end: Hhmm): string {
  return `${fmtTime12(start)} to ${fmtTime12(end)}`;
}

/**
 * The same range for a column too narrow to hold the word "to", which is the
 * roster grid on a phone. Dropping the end time instead would leave a boss
 * unable to read when a shift finishes, which is worse than losing a word.
 */
export function fmtTimeRangeCompact(start: Hhmm, end: Hhmm): string {
  return `${fmtTime12(start)}-${fmtTime12(end)}`;
}

/** A timestamp from the API rendered in local 12-hour time: "4:08pm". */
export function fmtClock12(when: string | Date | null | undefined): string {
  if (!when) return '';
  const d = typeof when === 'string' ? new Date(when) : when;
  if (Number.isNaN(d.getTime())) return '';
  return fmtTime12(`${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`);
}

export function fmtDateTime12(when: string | Date | null | undefined): string {
  if (!when) return '';
  const d = typeof when === 'string' ? new Date(when) : when;
  if (Number.isNaN(d.getTime())) return '';
  return `${fmtDateMedium(toIso(d))}, ${fmtClock12(d)}`;
}

/** Minutes between two HH:MM values net of a break, overnight aware. Mirrors the
 *  engine's `shiftMinutes` so the optimistic totals match what the server sends
 *  back a moment later. */
export function shiftMinutes(start: Hhmm, end: Hhmm, breakMinutes = 0): number {
  const [sh = 0, sm = 0] = start.split(':').map(Number);
  const [eh = 0, em = 0] = end.split(':').map(Number);
  let mins = eh * 60 + em - (sh * 60 + sm);
  if (mins <= 0) mins += 24 * 60;
  return Math.max(0, mins - breakMinutes);
}

/** "8h 30m", the shape a boss scans down a totals column. */
export function fmtDuration(minutes: number): string {
  const m = Math.max(0, Math.round(minutes));
  const h = Math.floor(m / 60);
  const rem = m % 60;
  if (h === 0) return `${rem}m`;
  if (rem === 0) return `${h}h`;
  return `${h}h ${rem}m`;
}

/** Decimal hours for pay tables: "7.25". */
export function fmtHours(minutes: number): string {
  return (Math.round((minutes / 60) * 100) / 100).toFixed(2);
}

export function fmtMoney(n: number | string | null | undefined, opts: { cents?: boolean } = {}): string {
  const v = typeof n === 'string' ? Number(n) : (n ?? 0);
  const safe = Number.isFinite(v) ? v : 0;
  const cents = opts.cents ?? true;
  return `$${safe.toLocaleString('en-AU', {
    minimumFractionDigits: cents ? 2 : 0,
    maximumFractionDigits: cents ? 2 : 0,
  })}`;
}

/** Compact money for a totals bar where the cents are noise: "$4,182". */
export function fmtMoneyShort(n: number | string | null | undefined): string {
  return fmtMoney(n, { cents: false });
}

/** Elapsed time for the live clock-on timer: "2:07:41". */
export function fmtElapsed(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

export function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return (parts[0] ?? '').slice(0, 2).toUpperCase();
  return `${(parts[0] ?? '')[0] ?? ''}${(parts[parts.length - 1] ?? '')[0] ?? ''}`.toUpperCase();
}

/**
 * A phone number that can never break across two lines, per the standing rule.
 * Non-breaking spaces go inside the number itself; `white-space: nowrap` on the
 * parent is not enough, because it forces overflow instead of wrapping and the
 * next component's CSS overrides it anyway.
 */
export function phoneDisplay(raw: string): string {
  return raw.trim().replace(/\s+/g, ' ');
}

export function phoneHref(raw: string): string {
  return `tel:${raw.replace(/[^\d+]/g, '')}`;
}
